import { mcpWildcardsForServers } from '@augmented/core';

// Shared helper for building Claude Code's --allowedTools string (ENG-4487).
//
// The manager spawns claude for an agent from exactly ONE site today —
// persistent-session.ts, whose argv is reused verbatim by the Docker-isolated
// path. (The header here used to claim a second "one-shot `claude -p` for
// scheduled tasks + webapp direct chat". Both were retired — scheduled tasks in
// ENG-6849, direct chat in ENG-6850 — and now deliver into the live tmux
// session via a doorbell/notice queue or send-keys. Corrected in ENG-9660 so
// the next reader does not go looking for a spawn site that no longer exists.)
//
// The drift this file exists to prevent was real: the old one-shot paths forgot
// Skill and Agent, so integration skills under .claude/skills/integration-...
// were silently invisible during scheduled-task execution. Agents produced
// apologetic "no data sources connected" outputs when the skills were on disk
// and their API keys were in env vars — they just couldn't call the Skill tool.
//
// Invariant: every Claude Code invocation the manager spawns must include
// Skill, Agent, and ToolSearch. Their absence disables integration-skill
// activation, subagent dispatch, and MCP tool binding respectively, all
// without warning. Keep that list in one place so a new spawn site
// physically cannot miss them.
//
// ENG-5926: ToolSearch added. Modern Claude Code surfaces MCP tools via
// the deferred-tool registry — tools start as schemas in ToolSearch's
// catalog and bind on first invocation. Without ToolSearch in
// `--allowedTools`, the entire MCP toolchain is invisible to a
// dispatched sub-agent. Don's empirical evidence 2026-06-03 on
// agt-aws-1: every `mcp__*` call from a Task-dispatched sub-agent
// returned "No such tool available." despite the wildcards being in
// the sub-agent's `tools:` allowlist line. ToolSearch was the
// missing piece — parent uses it for first-tool-call binding,
// sub-agents inherit nothing if they don't have it. The parent
// session also benefits (its own MCP first-call binding goes through
// the same path). Adding it on every spawn closes the entire ENG-5897
// → ENG-5905 → ENG-5922 → ENG-5924 → ENG-5926 thread.

// Order is stable for test snapshots.
const BASE_TOOLS = ['Bash', 'Read', 'Write', 'Edit', 'Grep', 'Glob', 'Agent', 'Skill', 'ToolSearch'] as const;

/**
 * ENG-9660 — the three built-ins that can NEVER be removed, whatever a tool
 * policy says.
 *
 * This is the invariant above, made mechanical. A locked-down profile that
 * drops these produces an agent that looks completely healthy and silently
 * cannot use any integration: no skill activation (Skill), no subagent
 * dispatch (Agent), and — the worst of the three — no MCP tools at all
 * (ToolSearch, ENG-5926), because every `mcp__*` call returns "No such tool
 * available." with no error anywhere an operator would see it.
 *
 * A policy is therefore not free to name an arbitrary set: it names what to
 * REMOVE, and these are subtracted from what it is allowed to remove. Stated
 * as data rather than as a comment, because the comment has not been enough —
 * this same failure has recurred across ENG-5897 → ENG-5926.
 */
export const UNREMOVABLE_TOOLS = ['Agent', 'Skill', 'ToolSearch'] as const;

/**
 * ENG-9660 — the built-in tools a locked-down agent gives up.
 *
 * `Bash` is the one that matters and the one this ticket was raised for: today
 * every agent on the fleet gets it unconditionally, including `risk_tier: Low`
 * agents whose entire job is reading and writing files. `Write` and `Edit` are
 * offered too so a genuinely read-only profile is expressible.
 */
export type RemovableTool = 'Bash' | 'Write' | 'Edit';

export interface AgentToolPolicy {
  /**
   * Built-in tools to withhold from this agent. Anything in UNREMOVABLE_TOOLS
   * is ignored rather than honoured — see that constant for why.
   */
  remove: readonly RemovableTool[];
}

/**
 * ENG-9660 — the `--tools` value for a policy, or `null` when the agent is
 * unrestricted.
 *
 * `null` is load-bearing: the caller must then omit the flag entirely rather
 * than pass an empty string, because `--tools ""` means "disable ALL built-in
 * tools" to Claude Code, which is the opposite of what an absent policy means.
 * That distinction is the single easiest way to turn this feature into a
 * fleet-wide outage, so it is expressed in the type rather than left to a
 * caller's judgement.
 *
 * Verified against the shipped 2.1.258 binary rather than inferred:
 *   --tools "Read,Edit,Grep,Glob"      + --dangerously-skip-permissions → no Bash
 *   --tools "Bash,Read,Edit,Grep,Glob" + --dangerously-skip-permissions → Bash works
 * (the second run is the control: without it, "no Bash" would also be what a
 * broken flag looks like.)
 */
export function builtinToolsForPolicy(policy: AgentToolPolicy | null | undefined): string | null {
  if (!policy) return null;
  const removable = new Set<string>(policy.remove);
  // The invariant, enforced rather than documented.
  for (const keep of UNREMOVABLE_TOOLS) removable.delete(keep);
  if (removable.size === 0) return null;
  const kept = BASE_TOOLS.filter((t) => !removable.has(t));
  return kept.join(',');
}

/** ENG-9660: true when this policy actually withholds something. */
export function policyRemovesAnything(policy: AgentToolPolicy | null | undefined): boolean {
  return builtinToolsForPolicy(policy) !== null;
}

// Build the comma-separated allowedTools string for a Claude Code spawn.
// Each MCP server name becomes a wildcard pattern matching every tool that
// server exposes; plus the base built-ins (incl. ToolSearch for MCP
// lazy-load — see ENG-5926).
//
// ENG-9660: `policy` narrows the built-in half only. MCP wildcards are never
// touched by it — a locked-down agent is one that cannot run commands, not one
// cut off from its integrations, and conflating the two is exactly the silent
// breakage UNREMOVABLE_TOOLS exists to stop.
export function buildAllowedTools(
  mcpServerNames: readonly string[],
  policy?: AgentToolPolicy | null,
): string {
  // ENG-8181: this used to rewrite hyphens to underscores, on the stated
  // assumption that "Claude Code's allowedTools patterns use underscore-
  // separated names". They do not — a tool name carries the .mcp.json server
  // key verbatim (`mcp__direct-chat__direct_chat_reply`), so the rewritten
  // pattern matched nothing and every hyphenated server was silently dropped
  // from the spawn's permitted set. mcpWildcardsForServers emits the verbatim
  // form plus the historical underscored one; see mcp-tool-patterns.ts.
  const removable = new Set<string>(policy?.remove ?? []);
  for (const keep of UNREMOVABLE_TOOLS) removable.delete(keep);
  const builtins = BASE_TOOLS.filter((t) => !removable.has(t));
  return [...mcpWildcardsForServers(mcpServerNames), ...builtins].join(',');
}
